import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { paths } from "ui/routes";
import { registerUser } from "core/services/site/user";
import createPoller from "utils/createPoller";
import { cx } from "utils/join-classnames";
import { useFormValidation } from "ui/hooks/use-form-validation";
import { useSessionContext } from "ui/context/SessionContext";
import { authenticateUser, getLoggedIn } from "core/services/authserver";
import { Button, FormElement } from "ui/components";
import styles from "./Register.module.scss";

const Register: React.FC = () => {
    const sessionContext = useSessionContext();
    const navigate = useNavigate();

    const [pending, setPending] = React.useState<boolean>(false);
    const [confirm, setConfirm] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>("");
    const theForm = useFormValidation({
        email: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                isEmail: true,
            },
        },
        phone: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: false,
                isPhone: true,
            },
        },
        organization: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: false,
            },
        },
        password: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 8,
                maxLength: 20,
            },
        },
        passwordconfirm: {
            valid: false,
            touched: false,
            value: "",
            errorMsg: "",
            rules: {
                required: true,
                minLength: 8,
                maxLength: 20,
                isSameAsReference: "password",
            },
        },
    });

    const checkIfVerified = async (token: string): Promise<boolean> => {
        try {
            const loggedInInfo = await getLoggedIn(token);
            if (loggedInInfo.error || !loggedInInfo.value) {
                throw Error(loggedInInfo.error);
            }
            if (loggedInInfo.value.verified) {
                sessionContext.setVerified(true);
                return true;
            }
            return false;
        } catch (err: any) {
            throw Error(err);
        }
    };

    const pollVerification = async () => {
        let failureCount = 0;
        await createPoller(async () => {
            try {
                if (!sessionContext.session) {
                    throw Error("no session");
                }
                const isVerified = await checkIfVerified(
                    sessionContext.session.token,
                );
                if (!isVerified) {
                    return false;
                }
                navigate(paths.projectsPage, { replace: true });
                return true;
            } catch (err) {
                failureCount += 1;
                if (failureCount > 4) {
                    // Assume this fragment is broken and stop polling
                    return true;
                }
                return false;
            }
        }, [{ iterations: 5000, interval: 3000 }]);
    };

    React.useEffect(() => {
        if (!confirm) {
            return;
        }
        pollVerification();
    }, [confirm]);

    // Handle actions
    const handleRegistration = async () => {
        setPending(true);
        try {
            if (
                !theForm.formState.email.value ||
                !theForm.formState.organization.value ||
                !theForm.formState.password.value ||
                !theForm.formState.phone.value
            ) {
                throw Error("Missing value(s) from form");
            }
            const data = {
                email: theForm.formState.email.value.toString(),
                password: theForm.formState.password.value.toString(),
                projectName: theForm.formState.organization.value.toString(),
                organization: {
                    name: theForm.formState.organization.value.toString(),
                    phoneNumber: theForm.formState.phone.value.toString(),
                },
            };
            const response = await registerUser(data);
            if (response.error || !response.value) {
                setError("Someting went wrong, please try again");
                setPending(false);
                return;
            }
            const authResponse = await authenticateUser(
                theForm.formState.email.value.toString(),
                theForm.formState.password.value.toString(),
            );
            if (authResponse.error || !authResponse.value) {
                setError("Someting went wrong while logging in...");
                setPending(false);
                return;
            }
            await checkIfVerified(authResponse.value.token);
            sessionContext.setSession(authResponse.value);
        } catch (err: any) {
            setError(err);
        }

        setPending(false);
        setConfirm(true);
    };

    React.useEffect(() => {
        if (!theForm.formState.passwordconfirm.value) {
            return;
        }
        theForm.inputChangeHandler(
            "passwordconfirm",
            theForm.formState.passwordconfirm.value.toString(),
        );
    }, [theForm.formState.password.value]);

    const currentYear = new Date().getFullYear();

    return (
        <div className={styles.page}>
            <div className={styles.register}>
                <img
                    src="./static/img/logo-spoton.svg"
                    alt="SpotOn"
                    className={styles.register__logo}
                />
                {confirm && (
                    <>
                        <div className={styles.register__heading}>
                            Your account is ready!
                        </div>
                        <div className={styles.register__text}>
                            Check your inbox to confirm your email address and
                            start clipping right away{" "}
                        </div>
                    </>
                )}
                {!confirm && (
                    <>
                        {" "}
                        <div className={styles.register__heading}>
                            Get started right away
                        </div>
                        <div className={styles.register__text}>
                            Fill in the form to create a free account
                        </div>
                        {error !== "" && (
                            <div className={styles.errormsg}>{error}</div>
                        )}
                        <div className={cx(styles.register__form)}>
                            <div>
                                <div
                                    className={cx(
                                        pending ? styles.formpending : null,
                                    )}
                                >
                                    <FormElement
                                        title="Business Email Address *"
                                        onChange={(value) => {
                                            theForm.inputChangeHandler(
                                                "email",
                                                value,
                                            );
                                        }}
                                        defaultText="your@email.com"
                                        theme="dark"
                                        type="text"
                                        id="email"
                                        fieldState={theForm.formState.email}
                                        disabled={pending}
                                    />
                                    <FormElement
                                        title="Phone number"
                                        onChange={(value) => {
                                            theForm.inputChangeHandler(
                                                "phone",
                                                value,
                                            );
                                        }}
                                        defaultText="Your phone number"
                                        theme="dark"
                                        type="text"
                                        id="phone"
                                        fieldState={theForm.formState.phone}
                                        disabled={pending}
                                    />
                                    <FormElement
                                        title="Organization name"
                                        onChange={(value) => {
                                            theForm.inputChangeHandler(
                                                "organization",
                                                value,
                                            );
                                        }}
                                        defaultText="Your organization's name"
                                        theme="dark"
                                        type="text"
                                        id="organization"
                                        fieldState={
                                            theForm.formState.organization
                                        }
                                        disabled={pending}
                                    />
                                    <FormElement
                                        title="Password *"
                                        onChange={(value) => {
                                            theForm.inputChangeHandler(
                                                "password",
                                                value,
                                            );
                                        }}
                                        defaultText="Password"
                                        theme="dark"
                                        type="password"
                                        id="password"
                                        fieldState={theForm.formState.password}
                                        disabled={pending}
                                    />
                                    <FormElement
                                        title="Confirm password *"
                                        onChange={(value) => {
                                            theForm.inputChangeHandler(
                                                "passwordconfirm",
                                                value,
                                            );
                                        }}
                                        defaultText="Confirm password"
                                        theme="dark"
                                        type="password"
                                        id="passwordconfirm"
                                        fieldState={
                                            theForm.formState.passwordconfirm
                                        }
                                        disabled={pending}
                                    />
                                </div>

                                <Button
                                    type="primary"
                                    onClick={handleRegistration}
                                    disabled={!theForm.formValid || pending}
                                    className={cx(
                                        styles.register__btn,
                                        styles.register__btn__primary,
                                    )}
                                    pending={pending}
                                >
                                    {pending ? "Creating…" : "Create account"}
                                </Button>
                            </div>
                            <NavLink
                                to="/login"
                                className={styles.register__link}
                            >
                                Already have an account? <span>Login</span>
                            </NavLink>
                        </div>
                        <div className={styles.register__footer}>
                            <p>
                                &copy; {currentYear} SpotOn, part of{" "}
                                <a
                                    href="https://www.wearetriple.com"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Triple
                                </a>
                                . All right reserved.
                            </p>
                            <p>
                                <a
                                    href="https://try.spot-on.media/privacy-policy/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Privacy Policy
                                </a>
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Register;
